import React, { useState } from 'react';
import startOfTomorrow from 'date-fns/startOfTomorrow';
import { Typography, Card, Icon, Flex, color, Switch, Drawer, IconButton, Box } from '@kistech/kis-ui';
import { ActionOverlay } from 'components/ActionOverlay';
import { useActions } from 'hooks/useActions';
import { actions as kiosksActions, Kiosk, selectors as kiosksSelectors } from 'store/kiosks';
import { KioskHardware } from 'store/kioskHardware';
import { selectors as authSelectors } from 'store/auth';
import { orDash, formatHardwareTitle } from 'utils/stringFormat';
import { useSelector } from 'react-redux';
import { useStyles } from '../styled';
import {
  Typography as MuiTypography,
  Box as MuiBox,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid
} from '@mui/material';

export interface KioskHardwareStatusProps {
  isOpen: boolean;
  onClose: () => void;
  kiosk: Kiosk;
}

export const KioskHardwareStatus = (props: KioskHardwareStatusProps) => {
  const { isOpen, onClose, kiosk } = props;
  const { updateKiosk } = useActions({
    updateKiosk: kiosksActions.updateKiosk
  });
  const kioskData = useSelector(kiosksSelectors.selectKiosk);
  const admin = useSelector(authSelectors.selectAdmin);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [kioskStatus, setKioskStatus] = useState<string>(kiosk.status);
  const [readOnlyAdmin, setReadOnlyAdmin] = useState(false);

  // check for user is Read-Only Admin or not, if yes hide switch button and show text
  React.useEffect(() => {
    if (admin && admin.role === 'ReadOnlyAdmin') setReadOnlyAdmin(true);
  }, [admin]);

  const { changeStatusKiosk } = useActions({ changeStatusKiosk: kiosksActions.changeStatusKiosk });

  const onSwitchStatus = (checked: boolean) => {
    setShowConfirmation(true);

    const status = checked ? 'Active' : 'Inactive';
    // TODO: log event
    changeStatusKiosk({ kioskId: kiosk.id, status, adminId: admin ? admin.id : '' });
    setKioskStatus(status);

    updateKiosk({
      id: kiosk.id,
      status,
      unscheduledUntil: startOfTomorrow()
    });
  };

  const HardwareStatus = (Status: { type: string }) => {
    const status = Object.values(Status).toString();
    if (status === 'Inactive') {
      return <Icon name="error_circle" color={color.RED} />;
    } else if (status === 'Active') {
      return <Icon name="success_circle" color={color.GREEN} />;
    }
    return <Typography>--</Typography>;
  };

  const closeConfirmOverlay = () => {
    setShowConfirmation(false);
  };

  const getHardwareDisplayName = (kioskHardware: KioskHardware) => {
    const { type, configuration } = kioskHardware;

    if (type === 'printer' && configuration.displayName) {
      return `${configuration.displayName} ${formatHardwareTitle(type)}`;
    }

    return formatHardwareTitle(type);
  };

  const classes = useStyles({ showDrawer: isOpen })();

  return (
    <>
      {showConfirmation && (
        <ActionOverlay
          title={
            <MuiBox className={classes.kioskStatusChangeTitle}>
              <MuiTypography className={classes.kioskStatusTitle} style={{ fontWeight: 600 }}>
                {kiosk.name} is now {kioskStatus}
              </MuiTypography>
            </MuiBox>
          }
          description={
            <MuiBox className={classes.kioskStatusChangeDescription}>
              <MuiTypography className={classes.kioskStatusDescription}>
                {kiosk.name} will remain {kioskStatus} until it resumes its scheduled operation tomorrow.
              </MuiTypography>
            </MuiBox>
          }
          confirmText="Ok"
          onConfirm={closeConfirmOverlay}
        />
      )}
      <Drawer isOpen={isOpen} onClose={onClose} hasBorderRadius>
        <Grid container direction="column" className={classes.kioskDetailsOverlay}>
          <Card fullHeight>
            <Grid className={classes.kioskOverlayHeaderBlock}>
              <Grid className={classes.kioskOverlayHeading}>
                <Flex>
                  <MuiTypography className={classes.kioskStatusHeading} style={{ fontWeight: 600 }}>
                    Kiosk Status
                  </MuiTypography>
                </Flex>
                <Flex>
                  <Switch
                    checked={kiosk.status === 'Active'}
                    onChange={(_e, checked) => onSwitchStatus(checked)}
                    data-testid="kiosk-status-switch"
                    checkedLabel="ACTIVE"
                    uncheckedLabel="INACTIVE"
                    isDisabled={!readOnlyAdmin ? false : true}
                  />
                </Flex>
              </Grid>
              <Grid className={classes.kioskOverlayCloseIcon}>
                <IconButton
                  name="exit_circle"
                  appearance="transparent"
                  size={40}
                  onClick={onClose}
                  data-testid="kiosk-status-x"
                />
              </Grid>
            </Grid>
            <Box padding="2 6" fullWidth bg="light-gray">
              <Typography weight={600} size={16}>
                {kiosk.name}
              </Typography>
            </Box>
            <Card.Divider color={color.BORDER} />
            <MuiBox className={classes.kioskTableBox}>
              <TableContainer id="kioskHardwareTable">
                <MuiTable stickyHeader style={{ borderCollapse: 'collapse' }}>
                  <TableHead style={{ background: 'white' }}>
                    <TableRow>
                      <TableCell className={classes.kioskHardwareHeaderType} style={{ width: '250px' }}>
                        Type
                      </TableCell>
                      <TableCell className={classes.kioskHardwareHeaders} style={{ width: '250px' }}>
                        Serial Number
                      </TableCell>
                      <TableCell className={classes.kioskHardwareHeaders} style={{ width: '150px' }}>
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {((kioskData && kioskData.hardware) || []).map((kioskHardwareObj: KioskHardware) => {
                      return (
                        <TableRow className={classes.kioskHardwareTableRows}>
                          <TableCell className={classes.kioskHardwareType}>
                            <Typography size={16} weight={600}>
                              {getHardwareDisplayName(kioskHardwareObj)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography size={16}>{orDash(kioskHardwareObj.serialCode)}</Typography>
                          </TableCell>
                          <TableCell>
                            <HardwareStatus type={kioskHardwareObj.status} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </MuiTable>
              </TableContainer>
            </MuiBox>
          </Card>
        </Grid>
      </Drawer>
    </>
  );
};

export default KioskHardwareStatus;
